import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthenticationService } from './Authentication/authentication.service';
import { ToastService } from './toast.service';

export interface AppUser {
  id: number;
  authUserId?: number;
  name: string;
  username: string;
  email?: string;
  phoneNumber?: string;
  employeeNo?: string;
  designation?: string;
  roles?: { id: number; code: string; name: string }[];
  roleCodes?: string[];
  departmentId: number;
  departmentName?: string;
  department?: { id: number; name: string; code?: string };
  projects?: { id: number; name: string; code: string }[];
  projectIds?: number[];
  avatarUrl?: string;
  status?: string;
  active?: boolean;
  isAssignTickets?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly API_URL = `${environment.apiUrl}/users`;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly toastService = inject(ToastService);

  readonly users = signal<AppUser[]>([]);

  readonly currentUser = computed(() => {
    const user = this.authService.currentUser();
    return {
      id: user?.id || 1,
      name: user?.name || 'Savinda Admin',
      username: user?.username || 'admin',
      email: user?.email || 'admin@company.com',
      avatarUrl: user?.avatarUrl,
      role: user?.role || 'Employee',
      departmentId: user?.departmentId || 1,
    };
  });

  constructor() {
    if (this.authService.getAccessToken()) {
      this.loadUsers().subscribe();
    }
  }

  loadUsers(params?: { search?: string; departmentId?: number | string; role?: string; assignTicketsOnly?: boolean }): Observable<AppUser[]> {
    if (!this.authService.getAccessToken()) {
      return of([]);
    }

    let url = this.API_URL;
    const queryParts: string[] = ['limit=100'];
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    if (params?.departmentId && params.departmentId !== 'all') queryParts.push(`departmentId=${params.departmentId}`);
    if (params?.role) queryParts.push(`role=${params.role}`);
    if (params?.assignTicketsOnly) queryParts.push(`assignTicketsOnly=true`);

    if (queryParts.length > 0) {
      url += `?${queryParts.join('&')}`;
    }

    return this.http.get<any>(url).pipe(
      map((res) => {
        let items: any[] = [];
        if (res.items) items = res.items;
        else if (res.data?.items) items = res.data.items;
        else if (res.data && Array.isArray(res.data)) items = res.data;
        else if (Array.isArray(res)) items = res;

        return items.map((u: any) => {
          const roles = Array.isArray(u.roles) ? u.roles : [];
          const roleCodes = roles.map((r: any) => typeof r === 'string' ? r : (r.code || r.name));
          return {
            ...u,
            id: Number(u.id),
            roles: roles,
            roleCodes: roleCodes,
            departmentId: Number(u.departmentId || u.department?.id || 0),
            departmentName: u.department?.name || '',
            projects: u.projects || [],
            projectIds: u.projects ? u.projects.map((p: any) => Number(p.id)) : [],
            status: u.status || 'Active',
            isAssignTickets: u.isAssignTickets !== false,
          };
        });
      }),
      tap((userList: AppUser[]) => this.users.set(userList)),
      catchError(() => of([])),
    );
  }

  searchEmployeeCache(employeeNo: string): Observable<any[]> {
    return this.http.get<any>(`${this.API_URL}/search-employee-cache`, {
      params: { employeeNo },
    }).pipe(
      map((res) => (res.data ? res.data : res)),
      catchError(() => of([])),
    );
  }

  searchEmployeeCacheByName(name: string): Observable<any[]> {
    return this.http.get<any>(`${this.API_URL}/search-employee-cache`, {
      params: { search: name },
    }).pipe(
      map((res) => (res.data ? res.data : res)),
      catchError(() => of([])),
    );
  }

  createUser(userDto: {
    authUserId: number;
    name: string;
    username: string;
    email?: string;
    phoneNumber?: string;
    password?: string;
    employeeNo?: string;
    designation?: string;
    roleCodes?: string[];
    roleIds?: number[];
    departmentId: number;
    projectIds?: number[];
    isAssignTickets?: boolean;
  }): Observable<AppUser> {
    return this.http.post<any>(this.API_URL, userDto).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.loadUsers().subscribe();
        this.toastService.success('User Created Successfully');
      }),
    );
  }

  updateUser(id: number | string, updates: any): Observable<AppUser> {
    return this.http.patch<any>(`${this.API_URL}/${id}`, updates).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.loadUsers().subscribe();
        this.toastService.success('User Updated Successfully');
      }),
    );
  }

  deleteUser(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`).pipe(
      tap(() => {
        this.loadUsers().subscribe();
        this.toastService.info('User Deleted');
      }),
    );
  }
}
